import { Component, TemplateFunction } from '@ribajs/core'
import { TwService } from '../modules/tailwind/services'
import { Breakpoint } from '../modules/tailwind/types'

interface Item {
  active: boolean
  el: HTMLElement
}

/**
 * The types for the scope
 */
interface Scope {
  items: Item[]
  interval: number
  activeIndex: number
}

export class AnnouncementBarComponent extends Component {
  /**
   * The tag name of the element, in this case `<sb-announcement-bar foobar="hello">...</sb-announcement-bar>`
   */
  public static tagName = 'sb-announcement-bar'

  protected tailwind = TwService.getSingleton()

  protected intervalId: number | null = null

  /**
   * Everything that is assigned to the scope can be used within the template with the Riba template engine.
   * Examples:
   * - Methods can be called with `<div rv-on-click="show"></div>`, this will call the show method defined in the scope
   * - Properties can be used with `<div rv-click-active="visible"></div>`, this will add the class `active` if visible is `true` and remove the class `active` if visible is `false`,
   * - Templates can be used with `<div rv-template="body"></div>`, `<div rv-html="body"></div>` or `<div rv-text="body"></div>`, this will be add the template, html or string to the innerHTML of the element. But you can only use the Riba Template Engine with `rv-template`
   */
  public scope: Scope = {
    // OPTIONS
    items: [],
    interval: 5000,

    // State
    activeIndex: -1,

    // METHODS

    // TEMPLATES
  }

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return []
  }

  /**
   * Attributes that must be passed to the custom element, if not all attributes are truthy,
   * the component will not be initialized and an error message will be issued.
   * @returns
   */
  protected requiredAttributes(): string[] {
    return []
  }

  /**
   * Called as soon as this custom element es registered as a available custom element
   */
  constructor() {
    super()
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(AnnouncementBarComponent.observedAttributes)
    this.addEventListeners()
  }

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  async beforeBind() {
    const children = Array.from(this.children)
    for (const child of children) {
      const item: Item = {
        active: false,
        el: child as HTMLElement,
      }
      this.scope.items.push(item)
    }
    await super.beforeBind()
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    await super.afterBind()
    this.enableNextItem()
    this.startInterval()
    this.classList.add(`${AnnouncementBarComponent.tagName}-ready`)
  }

  startInterval() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
    this.intervalId = setInterval(() => {
      this.enableNextItem()
    }, this.scope.interval)
  }

  protected enableNextItem() {
    const currentItem = this.scope.items[this.scope.activeIndex]
    if (currentItem) {
      currentItem.active = false
      currentItem.el.classList.remove('active')
    }

    let newActiveIndex = this.scope.activeIndex + 1
    if (newActiveIndex >= this.scope.items.length) {
      newActiveIndex = 0
    }
    this.scope.activeIndex = newActiveIndex

    const newItem = this.scope.items[newActiveIndex]
    newItem.active = true
    newItem.el.classList.add('active')
  }

  protected onBreakpointChanged(bp: Breakpoint) {
    this.debug('onBreakpointChanged', bp)
  }

  protected addEventListeners() {
    this.tailwind.on('breakpoint:changed', this.onBreakpointChanged, this)
  }

  protected removeEventListeners() {
    this.tailwind.off('breakpoint:changed', this.onBreakpointChanged, this)
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced, otherwise the innerHTML is replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
